import { createContext, useContext } from "react";

/**
 * Interface for Coral comment counts data with optional node references
 */
export interface CoralDataWithNodes {
  [coralId: string]: {
    count: number | null;
    nodeId?: string; // Optional node ID for reference if needed
  };
}

/**
 * Interface for Coral comment counts service
 */
export interface CoralService {
  getCommentCount: (coralId: string) => number | null;
  updateCommentCount: (
    coralId: string,
    count: number,
    timestamp: number,
  ) => void;
  subscribeToCommentCount: (
    coralId: string,
    callback: (count: number) => void,
  ) => () => void;
  registerCountId: (coralId: string) => void;
  loadAllCountsFromIndexedDB: () => Promise<void>;
  getLastReadCount: (coralId: string) => Promise<number | null>;
  setLastReadCount: (coralId: string, count: number) => Promise<void>;
}

/**
 * Context for Coral comment counts service.
 * Provides access to the coralService singleton instance.
 */
const CoralCommentsContext = createContext<CoralService | null>(null);

/**
 * Hook to access the Coral comment counts service.
 * @throws Error if used outside of CoralCountsProvider
 */
export const useCoralCommentsContext = (): CoralService => {
  const context = useContext(CoralCommentsContext);

  if (!context) {
    throw new Error(
      "useCoralCommentsContext must be used within a CoralCountsProvider",
    );
  }

  return context;
};

export default CoralCommentsContext;
